(function () {
  'use strict';

  const BADGE_SELECTOR = '[data-ht-ships-by]';
  const DATE_SELECTOR = '[data-ht-ships-by-date]';
  const LOCAL_CUTOFF_HOUR = 12;

  function dateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function startOfLocalDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  function addLocalDays(date, days) {
    const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    next.setDate(next.getDate() + days);
    return next;
  }

  function nthWeekdayOfMonth(year, month, weekday, occurrence) {
    const first = new Date(year, month, 1);
    const offset = (7 + weekday - first.getDay()) % 7;
    return new Date(year, month, 1 + offset + ((occurrence - 1) * 7));
  }

  function lastWeekdayOfMonth(year, month, weekday) {
    const last = new Date(year, month + 1, 0);
    const offset = (7 + last.getDay() - weekday) % 7;
    return new Date(year, month, last.getDate() - offset);
  }

  function observedHoliday(date) {
    if (date.getDay() === 6) return addLocalDays(date, -1);
    if (date.getDay() === 0) return addLocalDays(date, 1);
    return date;
  }

  function getUnitedStatesHolidayKeys(year) {
    const holidays = [
      observedHoliday(new Date(year, 0, 1)),
      nthWeekdayOfMonth(year, 0, 1, 3),
      nthWeekdayOfMonth(year, 1, 1, 3),
      lastWeekdayOfMonth(year, 4, 1),
      observedHoliday(new Date(year, 5, 19)),
      observedHoliday(new Date(year, 6, 4)),
      nthWeekdayOfMonth(year, 8, 1, 1),
      nthWeekdayOfMonth(year, 9, 1, 2),
      observedHoliday(new Date(year, 10, 11)),
      nthWeekdayOfMonth(year, 10, 4, 4),
      observedHoliday(new Date(year, 11, 25)),
    ];

    return new Set(holidays.map(dateKey));
  }

  function parseClosureDates(value) {
    return new Set(
      String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter((item) => /^\d{4}-\d{2}-\d{2}$/.test(item))
    );
  }

  function isHoliday(date) {
    const key = dateKey(date);
    const year = date.getFullYear();
    return [year - 1, year, year + 1].some((holidayYear) => getUnitedStatesHolidayKeys(holidayYear).has(key));
  }

  function isBusinessDay(date, closureDates) {
    const day = date.getDay();
    return day !== 0 && day !== 6 && !isHoliday(date) && !closureDates.has(dateKey(date));
  }

  function isAfterLocalCutoff(date) {
    if (date.getHours() > LOCAL_CUTOFF_HOUR) return true;
    if (date.getHours() < LOCAL_CUTOFF_HOUR) return false;
    return date.getMinutes() > 0 || date.getSeconds() > 0 || date.getMilliseconds() > 0;
  }

  function shouldHideBadge(now) {
    const day = now.getDay();
    if (day === 0 || day === 6) return true;
    return day === 5 && isAfterLocalCutoff(now);
  }

  function calculateShipDate(now, closureDates) {
    let shipDate = startOfLocalDay(now);

    if (!isBusinessDay(shipDate, closureDates) || isAfterLocalCutoff(now)) {
      do {
        shipDate = addLocalDays(shipDate, 1);
      } while (!isBusinessDay(shipDate, closureDates));
    }

    return shipDate;
  }

  function formatShipDate(date) {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }

  function formatShipLabel(shipDate, now) {
    return dateKey(shipDate) === dateKey(now) ? 'Today' : formatShipDate(shipDate);
  }

  function updateBadge(badge, now) {
    const dateNode = badge.querySelector(DATE_SELECTOR);
    if (!dateNode) return;

    const currentTime = now || new Date();
    badge.dataset.htShipsByTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';

    if (shouldHideBadge(currentTime)) {
      badge.hidden = true;
      badge.style.setProperty('display', 'none', 'important');
      badge.setAttribute('aria-hidden', 'true');
      delete badge.dataset.htShipsByDate;
      return;
    }

    const closureDates = parseClosureDates(badge.dataset.htFulfillmentClosures);
    const shipDate = calculateShipDate(currentTime, closureDates);
    dateNode.textContent = formatShipLabel(shipDate, currentTime);
    badge.dataset.htShipsByDate = dateKey(shipDate);
    badge.hidden = false;
    badge.style.removeProperty('display');
    badge.setAttribute('aria-hidden', 'false');
  }

  function updateAll(now) {
    if (typeof document === 'undefined') return;
    document.querySelectorAll(BADGE_SELECTOR).forEach((badge) => updateBadge(badge, now));
  }

  if (typeof document !== 'undefined') {
    const init = () => updateAll(new Date());
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
    window.addEventListener('pageshow', init);
    document.addEventListener('shopify:section:load', init);
    window.setInterval(init, 60000);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      LOCAL_CUTOFF_HOUR,
      dateKey,
      parseClosureDates,
      isHoliday,
      isBusinessDay,
      isAfterLocalCutoff,
      shouldHideBadge,
      calculateShipDate,
      formatShipLabel,
      updateBadge,
    };
  }
})();

